import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Job } from "@/types";
import { buildJobSlug } from "@/utils/jobSlug";

const FEATURED_MAX = 10;
const FEATURED_PER_EMPLOYER = 3;

export type JobsSort = 'relevance' | 'newest' | 'salary_high' | 'salary_low';

interface JobsQueryParams {
  page?: number;
  pageSize?: number;
  jobTitle?: string;
  location?: string;
  isRemote?: boolean;
  isInternship?: boolean;
  isContract?: boolean;
  isPartTime?: boolean;
  softwareBenefits?: string;
  sort?: JobsSort;
}

// Helper to transform Supabase job data to Job type
const transformJobData = (job: any, isFeatured: boolean = false): Job => ({
  id: job.id,
  featuredTier: isFeatured ? (job.featured_tier ?? 'standard') : null,
  type: 'Full Time',
  url: `https://jobs.auditfriendly.co/jobs/${buildJobSlug({ id: job.id, title: job.title, company: job.company, is_remote: job.is_remote })}`,
  created_at: job.post_date,
  company: job.company,
  company_url: null,
  location: job.display_location || job.location,
  display_location: job.display_location ?? null,
  display_salary: job.display_salary ?? null,
  title: job.title,
  description: '',
  how_to_apply: 'Apply through our website',
  company_logo: job.logo || null,
  salary: job.display_salary ?? '',
  is_remote: job.is_remote || false,
  logo: job.logo ? (job.logo.startsWith('http') ? job.logo : `https://${job.logo}`) : undefined,
  apply_link: job.apply_link || undefined,
  post_date: new Date(job.post_date),
  requirements: [],
  isFeatured,
  hours: job.hours || 'Full-Time',
  // Legacy properties
  postDate: new Date(job.post_date),
  isRemote: job.is_remote || false,
  applyLink: job.apply_link || undefined,
});

export const useJobs = (params: JobsQueryParams = {}) => {
  const {
    page = 1,
    pageSize = 10,
    jobTitle,
    location,
    isRemote,
    isInternship,
    isContract,
    isPartTime,
    softwareBenefits,
    sort = 'relevance',
  } = params;

  const queryParam = jobTitle?.trim() || null;
  const locationParam = location?.trim() || null;
  const remoteParam = isRemote ? true : null;

  const hours: string[] = [];
  if (isInternship) hours.push('Internship');
  if (isContract) hours.push('Contract');
  if (isPartTime) hours.push('Part-Time');
  const hoursParam = hours.length > 0 ? hours : null;

  const keywordsParam =
    softwareBenefits && softwareBenefits.trim().length > 0
      ? softwareBenefits.split(',').map((s) => s.trim()).filter(Boolean)
      : null;

  // Featured jobs query — sponsored, no search filters applied. Runs in
  // parallel with the regular query; selects only the columns the card
  // renders (full rows average ~20 KB each — job_full_text etc.).
  const featuredJobsQuery = useQuery({
    queryKey: ['featuredJobs'],
    queryFn: async () => {
      if (page !== 1) return [];

      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, company, location, display_location, display_salary, is_remote, logo, apply_link, post_date, hours, featured_tier')
        .eq('is_featured', true)
        .eq('is_duplicate', false)
        .or('is_archived.is.null,is_archived.eq.false')
        // 'standard' (paid recruiter promos) sorts before 'lite' (partner promos).
        .order('featured_tier', { ascending: false, nullsFirst: false })
        .order('post_date', { ascending: false })
        .limit(60);

      if (error) throw error;
      // At most 3 featured rows per employer, so one partner cannot fill the
      // top of the board. The rest stay on the board as ordinary rows.
      const perEmployer = new Map<string, number>();
      const capped = (data || []).filter((job) => {
        const key = (job.company || '').trim().toLowerCase();
        const n = perEmployer.get(key) ?? 0;
        perEmployer.set(key, n + 1);
        return n < FEATURED_PER_EMPLOYER;
      });
      return capped.slice(0, FEATURED_MAX).map((job) => transformJobData(job, true));
    },
    staleTime: 60000,
  });

  // Paginated regular jobs via search_jobs RPC. Independent of the featured
  // query (fixed offsets), so both fire on mount instead of serializing.
  // Page 1 shows featured cards on top of a full page of regular results.
  const jobsQuery = useQuery({
    queryKey: ['jobs', page, pageSize, queryParam, locationParam, remoteParam, hoursParam, keywordsParam, sort],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_jobs', {
        p_query: queryParam,
        p_location: locationParam,
        p_is_remote: remoteParam,
        p_metro_area: null,
        p_salary_min: null,
        p_salary_max: null,
        p_hours: hoursParam,
        p_keywords: keywordsParam,
        p_limit: pageSize,
        p_offset: (page - 1) * pageSize,
        p_sort: sort,
      } as any);

      if (error) throw error;

      return ((data as any[] | null) ?? []).map((job) => transformJobData(job, false));
    },
    staleTime: 30000,
  });

  // Total count via search_jobs_count RPC
  const countQuery = useQuery({
    queryKey: ['jobsCount', queryParam, locationParam, remoteParam, hoursParam, keywordsParam],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('search_jobs_count', {
        p_query: queryParam,
        p_location: locationParam,
        p_is_remote: remoteParam,
        p_metro_area: null,
        p_salary_min: null,
        p_salary_max: null,
        p_hours: hoursParam,
        p_keywords: keywordsParam,
      } as any);

      if (error) throw error;
      return Number(data ?? 0);
    },
    staleTime: 30000,
  });

  // Combine featured and regular jobs on page 1, dropping regular rows that
  // duplicate a featured card.
  const featuredIds = new Set((featuredJobsQuery.data || []).map((j) => j.id));
  const combinedJobs = page === 1
    ? [
        ...(featuredJobsQuery.data || []),
        ...(jobsQuery.data || []).filter((j) => !featuredIds.has(j.id)),
      ]
    : jobsQuery.data || [];

  return {
    data: combinedJobs,
    featuredJobs: featuredJobsQuery.data || [],
    isLoading: jobsQuery.isLoading || featuredJobsQuery.isLoading,
    isFetching: jobsQuery.isFetching || countQuery.isFetching || featuredJobsQuery.isFetching,
    totalCount: countQuery.data || 0,
    isError: jobsQuery.isError,
    error: jobsQuery.error,
  };
};
